import React, { useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import UIButton from 'components/ui/UIButton';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ideasActions, IdeaSort } from 'features/feedback/store/ideas.slice';
import { feedbackActions } from 'features/feedback/store/feedback.slice';
import IdeaCard from 'features/feedback/ideas/IdeaCard';

const SortTab: React.FC<{ active: boolean; label: string; onClick: () => void }> = ({ active, label, onClick }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    sx={{
      px: 2, py: 0.75, borderRadius: '999px', cursor: 'pointer', fontSize: 14, fontWeight: 600,
      background: active ? 'rgba(59,44,147,.06)' : 'transparent',
      color: active ? '#3B2C93' : '#7A7F88',
    }}
  >
    {label}
  </Box>
);

const IdeasBoardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, sort, hasMore, loading, page } = useAppSelector((s) => s.ideas);

  useEffect(() => {
    dispatch(ideasActions.ideasGet({ sort, reset: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const changeSort = (next: IdeaSort) => {
    if (next !== sort) dispatch(ideasActions.setSort(next));
  };
  const loadMore = () => dispatch(ideasActions.ideasGet({ sort, page: page + 1 }));
  const propose = () => {
    dispatch(feedbackActions.openDrawer());
    dispatch(feedbackActions.pickType('IDEA'));
  };

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto', width: '100%', p: 2 }}>
      <Box sx={{ border: '1px solid #E7E7E7', borderRadius: '18px', p: '26px', background: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ fontSize: 22, fontWeight: 800, flex: 1 }}>Ідеї спільноти</Box>
          <Box sx={{ display: 'flex', gap: 0.5, background: '#f5f6f8', borderRadius: '999px', p: 0.5 }}>
            <SortTab active={sort === 'top'} label="Популярні" onClick={() => changeSort('top')} />
            <SortTab active={sort === 'new'} label="Нові" onClick={() => changeSort('new')} />
          </Box>
          <UIButton variant="outlined" startIcon={<AddIcon />} onClick={propose}>Запропонувати</UIButton>
        </Box>

        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'rgba(39,204,188,.14)' }}>
              <LightbulbIcon sx={{ color: '#12a596' }} />
            </Box>
            <Box sx={{ fontSize: 18, fontWeight: 700 }}>Ще немає ідей</Box>
            <Box sx={{ fontSize: 14, color: '#7A7F88' }}>Стань першим — запропонуй, що покращити.</Box>
            <UIButton variant="contained" onClick={propose} sx={{ mt: 1 }}>Запропонувати ідею</UIButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((idea) => <IdeaCard key={idea.id} idea={idea} />)}
            {hasMore && (
              <UIButton variant="outlined" disabled={loading} onClick={loadMore} sx={{ alignSelf: 'center', mt: 1 }}>
                {loading ? 'Завантаження…' : 'Завантажити ще'}
              </UIButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default IdeasBoardPage;
